import dayjs from "dayjs";
import { TType } from "../../types";
import { ILongShort, IStatistics, TOrder } from "@root/common";

type TSeconds = number;
type TMinutes = number;


export function getGeneralTitles(type: TType, statistics: IStatistics | undefined, volume: number, inCoins?: boolean) {
    let spread = statistics && 'short' in statistics ? (parseFloat(statistics?.short?.current_funding) - parseFloat(statistics?.long?.current_funding)).toFixed(2) : NaN
    if (type === 'spot' && statistics && "short" in statistics) {
        spread = statistics?.short?.current_funding || NaN
    }
    if (type === 'margin' && statistics && 'short' in statistics) {
        spread = statistics?.short?.current_funding ? (-statistics?.short?.current_funding || NaN) : (statistics?.short?.current_funding || NaN)
    }
    return {
        "Вход": statistics ? `${getEnterSpread(statistics, volume, inCoins)}%` : "-",
        "Выход": statistics ? `${getCloseSpread(statistics, volume, inCoins)}%` : "-",
        "Спред": !Number.isNaN(spread) ? `${spread}%` : '-',
        "Комиссия": statistics && 'fee' in statistics ? getValue(statistics?.fee || '') : '',
    }
}

export function getLongShortTitles (longShort: ILongShort | undefined): Record<string, string | undefined> {
    if (!longShort) {
        return {}
    }

    return {
        "Ставка": getValue(longShort.current_funding) || '-',
        // "Прогноз": getValue(longShort.next_funding) || '-' ,
        "Отклонение": getValue(longShort.mark_index) || '-' ,
        "Стакан": getValue(longShort.orderbook_spread) || '-',
        "Период": getValue(longShort.interval, 'ч') || '-' ,
        "Лимит": longShort.limit ? longShort.limit.toFixed(0) + '$' : '-'
    }
}

function getValue (val: string, postfix="%") {
    const parsed = parseFloat(val)

    if (Number.isNaN(parsed)) {
        return null
    }

    return `${parsed}${postfix}`
}

export function getEnterSpread(message: IStatistics, volume?: number, inCoins?: boolean) {
    if (!("long" in message)) {
      return "- "
    }

    const asks = message.long.orderbook?.asks
    const bids = message.short.orderbook?.bids

    if (!asks || !bids) {
        return "- "
    }

    const askPrice = inCoins ? findAvgPriceInCoins(asks, volume) : findAvgPrice(asks, volume)
    const bidPrice = inCoins ? findAvgPriceInCoins(bids, volume) : findAvgPrice(bids, volume)

    const spread = getSpread(askPrice, bidPrice)

    return spread
}

export function getCloseSpread(message: IStatistics, volume: number, inCoins?: boolean) {
    if (!('short' in message)) {
      return "- "
    }

    const asks = message?.short?.orderbook?.asks
    const bids = message?.long?.orderbook?.bids

    if (!asks || !bids) {
        return "- "
    }

    const askPrice = inCoins ? findAvgPriceInCoins(asks, volume) : findAvgPrice(asks, volume)
    const bidPrice = inCoins ? findAvgPriceInCoins(bids, volume) : findAvgPrice(bids, volume)

    return getSpread(askPrice, bidPrice)
}

export function getOrderPrices (message: IStatistics, volume: number) {
  if (!('short' in message)) {
    return;
  }

  const asks = message?.long?.orderbook?.asks
  const bids = message?.short?.orderbook?.bids

  if (!asks || !bids) {
      return;
  }

  const askPrice = findAvgPrice(asks, volume)
  const bidPrice = findAvgPrice(bids, volume)

  return {
    short_price: bidPrice,
    long_price: askPrice,
  }
}


export function getOrderPricesInCoins (message: IStatistics, volume: number) {
  if (!('short' in message)) {
    return;
  }

  const asks = message?.long?.orderbook?.asks
  const bids = message?.short?.orderbook?.bids

  if (!asks || !bids) {
      return;
  }

  const askPrice = findAvgPriceInCoins(asks, volume)
  const bidPrice = findAvgPriceInCoins(bids, volume)

  return {
    short_price: bidPrice,
    long_price: askPrice,
  }
}

export function getReverseOrderPrices (message: IStatistics, volume: number) {
  if (!('long' in message)) {
    return;
  }

  const asks = message?.short?.orderbook?.asks
  const bids = message?.long?.orderbook?.bids

  if (!asks || !bids) {
      return;
  }

  const askPrice = findAvgPrice(asks, volume)
  const bidPrice = findAvgPrice(bids, volume)

  return {
    short_price: askPrice,
    long_price: bidPrice,
  }
}

export function getReverseOrderPricesInCoins (message: IStatistics, volume: number) {
  if (!('long' in message)) {
    return;
  }

  const asks = message?.short?.orderbook?.asks
  const bids = message?.long?.orderbook?.bids

  if (!asks || !bids) {
      return;
  }

  const askPrice = findAvgPriceInCoins(asks, volume)
  const bidPrice = findAvgPriceInCoins(bids, volume)

  return {
    short_price: askPrice,
    long_price: bidPrice,
  }
}

function getSpread(askPrice: number, bidPrice: number) {
    return ((bidPrice - askPrice) / (askPrice + bidPrice) * 2 * 100).toFixed(2)
}

function findAvgPrice(orders: TOrder[], volume?: number) {
    volume = volume || 1

    let result = orders.reduce((acc, item: TOrder) => {
        if (acc.remain_volume <= 0) return acc

        const addVolume = Math.min(parseInt(item.volume), acc.remain_volume)

        return {
            ...acc,
            price_volume: acc.price_volume + item.price*addVolume,
            remain_volume: Math.round(acc.remain_volume - addVolume)
        }

    }, {price_volume: 0, remain_volume: volume})

    if (!result.price_volume) {
        result = {price_volume: Number(orders.at(0)?.price), remain_volume: 0}
        volume = 1
    }

    return result.price_volume / (volume - result.remain_volume)
}

function findAvgPriceInCoins(orders: TOrder[], coinVolume?: number) {
    coinVolume = coinVolume || 1

    let result = orders.reduce((acc, item: TOrder) => {
        if (acc.remain_volume <= 0) return acc

        const addVolume = Math.min(Number(item.volume)/Number(item.price), acc.remain_volume)

        return {
            ...acc,
            price_volume: acc.price_volume + item.price*addVolume,
            remain_volume: acc.remain_volume - addVolume
        }

    }, {price_volume: 0, remain_volume: coinVolume})

    if (!result.price_volume) {
        result = {price_volume: Number(orders.at(0)?.price), remain_volume: 0}
        coinVolume = 1
    }

    return result.price_volume / (coinVolume - result.remain_volume)
}
